import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Container as ContainerIcon, BookmarkPlus, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { CURRENCIES, PIECES_UNITS, EQUIPMENT_OPTIONS, COMMODITY_OPTIONS, ACCESSORIAL_OPTIONS, currencySymbol } from "@/lib/constants";
import CategoryField from "@/components/CategoryField";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import LocationFormDialog from "@/components/LocationFormDialog";
import AddressLookup from "@/components/AddressLookup";

const emptyAddr = { name: "", street: "", city: "", state: "", zip: "", contact_name: "", contact_phone: "" };
const emptyContainer = { container_number: "", pickup_date: "", drop_date: "", ingate_date: "" };
const emptyAccessorial = { type: "Fuel Surcharge", description: "", amount: "" };
const MAX_CONTAINERS = 5;

// datetime-local wants "YYYY-MM-DDTHH:mm" — normalise any incoming value.
const toDateTimeLocal = (v) => {
  if (!v) return "";
  const s = String(v);
  // date-only "YYYY-MM-DD" -> midnight
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00`;
  // already datetime-local
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return s.slice(0, 16);
  // ISO or other parseable value
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const isContainerEquipment = (eq) =>
  !!eq && /container|intermodal/i.test(String(eq));

const initial = {
  pickup: { ...emptyAddr },
  delivery: { ...emptyAddr },
  pickup_date: "",
  appointment_date: "",
  delivery_date: "",
  po_number: "",
  reference: "",
  service_type: "FTL",
  equipment: "Dry Van",
  commodity: "",
  weight: "",
  pieces: "",
  pieces_unit: "Pallets",
  containers: [],
  accessorials: [],
  rate: "",
  currency: "USD",
  notes: "",
  customer_id: "",
  bill_to_customer_id: "",
  carrier_id: "",
  carrier_name: "",
};

const AddressBlock = ({ title, value, onChange, prefix, kind, savedLocations = [], customerLocations = [], activeCustomerId = null, onSavedLocationsChanged, onCustomerLocationsChanged }) => {
  const upd = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const [savingLabel, setSavingLabel] = useState(null); // null = closed, "" = open
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveScope, setSaveScope] = useState("customer"); // "customer" or "shared"
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitial, setCreateInitial] = useState(null);

  const applyLocation = (loc) => {
    if (!loc) return;
    onChange({
      ...value,
      name: loc.name || "",
      street: loc.street || "",
      city: loc.city || "",
      state: loc.state || "",
      zip: loc.zip || "",
      contact_name: loc.contact_name || value.contact_name || "",
      contact_phone: loc.contact_phone || value.contact_phone || "",
    });
    toast.success(`Loaded "${loc.label}"`);
  };

  // Called when user types a name into autocomplete + hits "Create new"
  const openCreateFromSearch = (typed) => {
    setCreateInitial({
      label: typed,
      name: value.name || "",
      street: value.street || "",
      city: value.city || "",
      state: value.state || "",
      zip: value.zip || "",
      contact_name: value.contact_name || "",
      contact_phone: value.contact_phone || "",
    });
    setCreateOpen(true);
  };

  const handleSave = async () => {
    const label = (savingLabel || "").trim();
    if (!label) { toast.error("Give this location a name first"); return; }
    if (!value.street && !value.city) {
      toast.error("Enter at least a street or city before saving");
      return;
    }
    setSaveBusy(true);
    try {
      const payload = {
        label,
        kind,
        customer_id: saveScope === "customer" && activeCustomerId ? activeCustomerId : null,
        name: value.name || null,
        street: value.street || null,
        city: value.city || null,
        state: value.state || null,
        zip: value.zip || null,
        contact_name: value.contact_name || null,
        contact_phone: value.contact_phone || null,
      };
      const { data } = await api.post("/locations", payload);
      const scopeMsg = payload.customer_id ? "customer address book" : "shared library";
      toast.success(`Saved "${data.label}" to ${scopeMsg}`);
      setSavingLabel(null);
      if (payload.customer_id) onCustomerLocationsChanged?.();
      else onSavedLocationsChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save location");
    } finally {
      setSaveBusy(false);
    }
  };

  // Filter by kind (either match this address's kind, or "both")
  const filterByKind = (l) => !l.kind || l.kind === kind || l.kind === "both";
  const customerOptionsFiltered = customerLocations.filter(filterByKind);
  const sharedOptionsFiltered = savedLocations
    .filter(filterByKind)
    .filter((l) => !l.customer_id); // exclude other customers' — only the current customer's already shown above

  return (
    <div className="border border-zinc-200 p-3 bg-zinc-50">
      <div className="flex items-center justify-between mb-2">
        <div className="mono-label">{title}</div>
        <div className="flex items-center gap-2">
          <LocationAutocomplete
            options={sharedOptionsFiltered}
            customerOptions={customerOptionsFiltered}
            onSelect={applyLocation}
            onCreateNew={openCreateFromSearch}
            placeholder={kind === "pickup" ? "Search Customers…" : "Search Consignees…"}
            createLabel={kind === "pickup" ? "New Customer" : "New Consignee"}
            dataTestId={`${prefix}-load-saved`}
          />
          {savingLabel === null ? (
            <button
              type="button"
              onClick={() => setSavingLabel(value.name || "")}
              className="text-[11px] inline-flex items-center gap-1 text-[#0056B3] hover:underline"
              data-testid={`${prefix}-save-location-btn`}
            >
              <BookmarkPlus className="h-3 w-3" /> Save location
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <Input
                autoFocus
                value={savingLabel}
                onChange={(e) => setSavingLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); handleSave(); }
                  if (e.key === "Escape") setSavingLabel(null);
                }}
                placeholder="Name this location"
                className="sharp h-7 text-[11px] w-40 bg-white"
                data-testid={`${prefix}-save-label`}
              />
              {activeCustomerId && (
                <Select value={saveScope} onValueChange={setSaveScope}>
                  <SelectTrigger className="sharp h-7 w-28 text-[10px] bg-white" data-testid={`${prefix}-save-scope`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer" className="text-xs">For this customer</SelectItem>
                    <SelectItem value="shared" className="text-xs">Shared library</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <button
                type="button"
                disabled={saveBusy}
                onClick={handleSave}
                className="p-1 text-green-700 hover:text-green-900 disabled:opacity-50"
                title="Save"
                data-testid={`${prefix}-save-confirm`}
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setSavingLabel(null)}
                className="p-1 text-zinc-500 hover:text-zinc-900"
                title="Cancel"
                data-testid={`${prefix}-save-cancel`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="mono-label">Location name</label>
          <Input value={value.name || ""} onChange={upd("name")} className="sharp mt-1 apex-input" data-testid={`${prefix}-name`} />
        </div>
        <div className="col-span-2">
          <label className="mono-label">Street</label>
          <AddressLookup
            value={value.street}
            onChange={(v) => onChange({ ...value, street: v })}
            onSelect={(a) => onChange({
              ...value,
              street: a.street || value.street,
              city: a.city || value.city,
              state: a.state || value.state,
              zip: a.zip || value.zip,
            })}
            placeholder="Type address or business…"
            dataTestId={`${prefix}-street`}
          />
        </div>
        <div>
          <label className="mono-label">City</label>
          <Input required value={value.city} onChange={upd("city")} className="sharp mt-1 apex-input" data-testid={`${prefix}-city`} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mono-label">State</label>
            <Input required value={value.state} onChange={upd("state")} className="sharp mt-1 apex-input" data-testid={`${prefix}-state`} />
          </div>
          <div>
            <label className="mono-label">Zip</label>
            <Input required value={value.zip} onChange={upd("zip")} className="sharp mt-1 apex-input" data-testid={`${prefix}-zip`} />
          </div>
        </div>
        <div>
          <label className="mono-label">Contact</label>
          <Input value={value.contact_name || ""} onChange={upd("contact_name")} className="sharp mt-1 apex-input" />
        </div>
        <div>
          <label className="mono-label">Phone</label>
          <Input value={value.contact_phone || ""} onChange={upd("contact_phone")} className="sharp mt-1 apex-input" />
        </div>
      </div>
      <LocationFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        kind={kind}
        initial={createInitial}
        onSaved={(loc) => {
          onSavedLocationsChanged?.();
          if (loc?.customer_id) onCustomerLocationsChanged?.();
          applyLocation(loc);
        }}
      />
    </div>
  );
};

export default function JobFormDialog({ open, onOpenChange, onCreated, customers = null, carriers = null, asAdmin = false, job = null }) {
  const isEdit = !!job;
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [savedLocations, setSavedLocations] = useState([]);
  const [customerLocations, setCustomerLocations] = useState([]);

  const loadSavedLocations = async () => {
    if (!asAdmin) return;
    try {
      const { data } = await api.get("/locations");
      setSavedLocations(data || []);
    } catch {
      /* silent — feature is optional */
    }
  };

  const loadCustomerLocations = async (cid) => {
    if (!asAdmin || !cid) { setCustomerLocations([]); return; }
    try {
      const { data } = await api.get(`/customers/${cid}/locations`);
      setCustomerLocations(data || []);
    } catch {
      setCustomerLocations([]);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadSavedLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Re-fetch customer's saved locations whenever the selected customer changes.
  useEffect(() => {
    if (!open) return;
    loadCustomerLocations(form.customer_id || job?.customer_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.customer_id]);

  useEffect(() => {
    if (!open) return;
    if (job) {
      setForm({
        ...initial,
        pickup: { ...initial.pickup, ...(job.pickup || {}) },
        delivery: { ...initial.delivery, ...(job.delivery || {}) },
        pickup_date: toDateTimeLocal(job.pickup_date),
        appointment_date: toDateTimeLocal(job.appointment_date),
        delivery_date: toDateTimeLocal(job.delivery_date),
        po_number: job.po_number || "",
        reference: job.reference || "",
        service_type: job.service_type || "FTL",
        equipment: job.equipment || "Dry Van",
        commodity: job.commodity || "",
        weight: job.weight ?? "",
        pieces: job.pieces ?? "",
        pieces_unit: job.pieces_unit || "Pallets",
        containers: Array.isArray(job.containers)
          ? job.containers.map((c) => ({ ...emptyContainer, ...c }))
          : [],
        accessorials: Array.isArray(job.accessorials)
          ? job.accessorials.map((a) => ({
              type: a.type || "Other",
              description: a.description || "",
              amount: a.amount ?? "",
            }))
          : [],
        rate: job.rate ?? "",
        currency: job.currency || "USD",
        notes: job.notes || "",
        customer_id: job.customer_id || "",
        bill_to_customer_id: job.bill_to_customer_id || "",
        carrier_id: job.carrier_id || "",
        carrier_name: job.carrier_name || "",
      });
    } else {
      setForm(initial);
    }
  }, [open, job]);

  const submit = async (e) => {
    e.preventDefault();
    if (asAdmin && !isEdit && !form.customer_id) {
      toast.error("Please choose a customer");
      return;
    }
    setBusy(true);
    try {
      const containerRows = isContainerEquipment(form.equipment)
        ? form.containers
            .filter((c) => c && c.container_number && c.container_number.trim())
            .map((c) => ({
              container_number: c.container_number.trim(),
              pickup_date: c.pickup_date || null,
              drop_date: c.drop_date || null,
              ingate_date: c.ingate_date || null,
            }))
        : [];
      const accessorialRows = form.accessorials
        .filter((a) => a && a.type && (a.amount !== "" && Number(a.amount) !== 0))
        .map((a) => ({
          type: a.type,
          description: a.description || null,
          amount: Number(a.amount) || 0,
        }));
      const payload = {
        ...form,
        weight: form.weight ? Number(form.weight) : null,
        pieces: form.pieces ? Number(form.pieces) : null,
        rate: form.rate ? Number(form.rate) : null,
        containers: containerRows,
        accessorials: accessorialRows,
      };
      if (!asAdmin) {
        delete payload.customer_id;
        delete payload.bill_to_customer_id;
        delete payload.carrier_id;
        delete payload.carrier_name;
      } else {
        if (!payload.bill_to_customer_id) {
          delete payload.bill_to_customer_id;
        }
        // Carrier: pick from saved carrier OR type name; ensure only one path.
        if (payload.carrier_id) {
          // saved carrier — name will be snapshot server-side; don't send stale
          payload.carrier_name = "";
        } else {
          payload.carrier_id = "";
          payload.carrier_name = (payload.carrier_name || "").trim();
        }
      }
      if (isEdit) {
        // PATCH — cannot change customer_id after creation
        delete payload.customer_id;
        const { data } = await api.patch(`/jobs/${job.id}`, payload);
        toast.success(`Job ${data.job_number} updated`);
        onCreated?.(data);
      } else {
        const { data } = await api.post("/jobs", payload);
        toast.success(`Job ${data.job_number} created`);
        onCreated?.(data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sharp max-w-6xl max-h-[92vh] overflow-y-auto border-zinc-950">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight">
            {isEdit ? `Edit job ${job.job_number}` : "New job request"}
          </DialogTitle>
          <p className="text-xs text-zinc-600">
            {isEdit
              ? "Update pickup/delivery details, freight info, or dispatch fields. Customer cannot be changed after creation."
              : "Enter pickup and delivery details. Dispatch will confirm the appointment."}
          </p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {/* ROW 1 — Customer / Bill To / Carrier (saved) / Carrier (ad-hoc) */}
          {asAdmin && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 border border-zinc-950 bg-zinc-50 p-3" data-testid="dispatch-row">
              {customers && !isEdit && (
                <div>
                  <label className="mono-label">Customer *</label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger className="sharp mt-1 h-9 bg-white" data-testid="job-customer-select"><SelectValue placeholder="Choose customer" /></SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} — {c.company || c.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {customers && (
                <div>
                  <label className="mono-label whitespace-nowrap">Bill to</label>
                  <Select value={form.bill_to_customer_id} onValueChange={(v) => setForm({ ...form, bill_to_customer_id: v })}>
                    <SelectTrigger className="sharp mt-1 h-9 bg-white" data-testid={isEdit ? "job-billto-select-edit" : "job-billto-select"}><SelectValue placeholder="Same as customer" /></SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} — {c.company || c.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="mono-label">Saved carrier</label>
                <Select
                  value={form.carrier_id || "__none__"}
                  onValueChange={(v) => setForm({ ...form, carrier_id: v === "__none__" ? "" : v, carrier_name: v === "__none__" ? form.carrier_name : "" })}
                >
                  <SelectTrigger className="sharp mt-1 h-9 bg-white" data-testid="job-carrier-select">
                    <SelectValue placeholder="— none —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— none —</SelectItem>
                    {(carriers || []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}{c.mc_number ? ` · MC ${c.mc_number}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mono-label">Ad-hoc carrier</label>
                <Input
                  value={form.carrier_name}
                  onChange={(e) => setForm({ ...form, carrier_name: e.target.value, carrier_id: e.target.value ? "" : form.carrier_id })}
                  disabled={!!form.carrier_id}
                  className="sharp mt-1 h-9 apex-input bg-white"
                  placeholder={form.carrier_id ? "Using saved carrier" : "e.g. Fast Freight Inc."}
                  data-testid="job-carrier-name"
                />
              </div>
            </div>
          )}

          {/* ROW 2 — Dates + Ref / PO / Service / Equipment (dense 4-col) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="mono-label">Pickup date &amp; time</label>
              <Input type="datetime-local" value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })}
                className="sharp mt-1 h-9 apex-input" data-testid="job-pickup-date" />
            </div>
            <div>
              <label className="mono-label">Appointment</label>
              <Input type="datetime-local" value={form.appointment_date} onChange={(e) => setForm({ ...form, appointment_date: e.target.value })}
                className="sharp mt-1 h-9 apex-input" data-testid="job-appt" />
            </div>
            <div>
              <label className="mono-label">Delivery date &amp; time</label>
              <Input type="datetime-local" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
                className="sharp mt-1 h-9 apex-input" data-testid="job-delivery-date" />
            </div>
            <div>
              <label className="mono-label">Service</label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger className="sharp mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["FTL", "LTL", "Reefer", "Flatbed", "Drayage", "Expedited"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mono-label">PO #</label>
              <Input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                className="sharp mt-1 h-9 apex-input font-mono" data-testid="job-po" />
            </div>
            <div>
              <label className="mono-label">Reference</label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                className="sharp mt-1 h-9 apex-input font-mono" data-testid="job-ref" />
            </div>
            <div>
              <label className="mono-label">Equipment</label>
              <CategoryField
                value={form.equipment}
                onChange={(v) => setForm({ ...form, equipment: v })}
                options={EQUIPMENT_OPTIONS}
                placeholder="Select equipment"
                testId="job-equipment"
              />
            </div>
            <div>
              <label className="mono-label">Commodity</label>
              <CategoryField
                value={form.commodity}
                onChange={(v) => setForm({ ...form, commodity: v })}
                options={COMMODITY_OPTIONS}
                placeholder="Select commodity"
                testId="job-commodity"
              />
            </div>
          </div>

          {/* ROW 3 — Freight (Weight / Pieces / Unit / Rate) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="mono-label">Weight (lb)</label>
              <Input type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="sharp mt-1 h-9 apex-input font-mono" />
            </div>
            <div>
              <label className="mono-label">Pieces</label>
              <Input type="number" value={form.pieces} onChange={(e) => setForm({ ...form, pieces: e.target.value })} className="sharp mt-1 h-9 apex-input font-mono" data-testid="job-pieces" />
            </div>
            <div>
              <label className="mono-label">Unit</label>
              <Select value={form.pieces_unit} onValueChange={(v) => setForm({ ...form, pieces_unit: v })}>
                <SelectTrigger className="sharp mt-1 h-9" data-testid="job-pieces-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PIECES_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {asAdmin ? (
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="mono-label">Rate</label>
                  <Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })}
                    className="sharp mt-1 h-9 apex-input font-mono" data-testid="job-rate" />
                </div>
                <div>
                  <label className="mono-label">CUR</label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="sharp mt-1 h-9" data-testid="job-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.key}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : <div />}
          </div>

          {/* ROW 4 — Pickup & Delivery address blocks (2-col) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <AddressBlock title="PICKUP" value={form.pickup} onChange={(v) => setForm({ ...form, pickup: v })} prefix="pickup" kind="pickup" savedLocations={savedLocations} customerLocations={customerLocations} onSavedLocationsChanged={loadSavedLocations} onCustomerLocationsChanged={() => loadCustomerLocations(form.customer_id || job?.customer_id)} activeCustomerId={form.customer_id || job?.customer_id} />
            <AddressBlock title="DELIVERY" value={form.delivery} onChange={(v) => setForm({ ...form, delivery: v })} prefix="delivery" kind="delivery" savedLocations={savedLocations} customerLocations={customerLocations} onSavedLocationsChanged={loadSavedLocations} onCustomerLocationsChanged={() => loadCustomerLocations(form.customer_id || job?.customer_id)} activeCustomerId={form.customer_id || job?.customer_id} />
          </div>

          {isContainerEquipment(form.equipment) && (
            <div className="border border-zinc-950 bg-zinc-50 p-3" data-testid="containers-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ContainerIcon className="h-4 w-4 text-zinc-700" />
                  <div className="mono-label !text-zinc-900">Containers ({form.containers.length}/{MAX_CONTAINERS})</div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={form.containers.length >= MAX_CONTAINERS}
                  onClick={() => setForm({ ...form, containers: [...form.containers, { ...emptyContainer }] })}
                  className="sharp h-8 border-zinc-400 bg-white"
                  data-testid="add-container"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add container
                </Button>
              </div>
              {form.containers.length === 0 ? (
                <p className="mt-2 text-xs text-zinc-600">
                  Add up to {MAX_CONTAINERS} container numbers with their pickup, drop and in-gate dates.
                </p>
              ) : (
                <div className="mt-2 space-y-2">
                  {form.containers.map((c, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white border border-zinc-200 p-2" data-testid={`container-row-${idx}`}>
                      <div className="col-span-12 md:col-span-3">
                        <label className="mono-label !text-[9px]">Container #</label>
                        <Input
                          value={c.container_number}
                          onChange={(e) => {
                            const next = [...form.containers];
                            next[idx] = { ...next[idx], container_number: e.target.value.toUpperCase() };
                            setForm({ ...form, containers: next });
                          }}
                          className="sharp mt-1 h-9 apex-input font-mono"
                          placeholder="ABCD1234567"
                          data-testid={`container-number-${idx}`}
                        />
                      </div>
                      <div className="col-span-6 md:col-span-3">
                        <label className="mono-label !text-[9px]">Pickup date</label>
                        <Input
                          type="date"
                          value={c.pickup_date || ""}
                          onChange={(e) => {
                            const next = [...form.containers];
                            next[idx] = { ...next[idx], pickup_date: e.target.value };
                            setForm({ ...form, containers: next });
                          }}
                          className="sharp mt-1 h-9 apex-input"
                          data-testid={`container-pickup-${idx}`}
                        />
                      </div>
                      <div className="col-span-6 md:col-span-3">
                        <label className="mono-label !text-[9px]">Drop date</label>
                        <Input
                          type="date"
                          value={c.drop_date || ""}
                          onChange={(e) => {
                            const next = [...form.containers];
                            next[idx] = { ...next[idx], drop_date: e.target.value };
                            setForm({ ...form, containers: next });
                          }}
                          className="sharp mt-1 h-9 apex-input"
                          data-testid={`container-drop-${idx}`}
                        />
                      </div>
                      <div className="col-span-10 md:col-span-2">
                        <label className="mono-label !text-[9px]">In-gate date</label>
                        <Input
                          type="date"
                          value={c.ingate_date || ""}
                          onChange={(e) => {
                            const next = [...form.containers];
                            next[idx] = { ...next[idx], ingate_date: e.target.value };
                            setForm({ ...form, containers: next });
                          }}
                          className="sharp mt-1 h-9 apex-input"
                          data-testid={`container-ingate-${idx}`}
                        />
                      </div>
                      <div className="col-span-2 md:col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, containers: form.containers.filter((_, i) => i !== idx) })}
                          className="h-9 w-9 flex items-center justify-center border border-zinc-300 text-rose-700 hover:bg-rose-50"
                          title="Remove container"
                          data-testid={`remove-container-${idx}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {asAdmin && (
            <div className="border border-zinc-950 bg-zinc-50 p-3" data-testid="accessorials-section">
              <div className="flex items-center justify-between">
                <div className="mono-label !text-zinc-900">
                  Accessorial Charges ({form.accessorials.length})
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setForm({ ...form, accessorials: [...form.accessorials, { ...emptyAccessorial }] })}
                  className="sharp h-8 border-zinc-400 bg-white"
                  data-testid="add-accessorial"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add charge
                </Button>
              </div>
              {form.accessorials.length === 0 ? (
                <p className="mt-2 text-xs text-zinc-600">
                  Add extra charges (fuel surcharge, detention, lumper, etc.) — they&apos;ll appear as separate line items on the invoice.
                </p>
              ) : (
                <>
                  <div className="mt-2 space-y-2">
                    {form.accessorials.map((a, idx) => {
                      const isOther = a.type === "Other";
                      return (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white border border-zinc-200 p-2" data-testid={`accessorial-row-${idx}`}>
                          <div className="col-span-12 md:col-span-4">
                            <label className="mono-label !text-[9px]">Type</label>
                            <Select
                              value={a.type}
                              onValueChange={(v) => {
                                const next = [...form.accessorials];
                                next[idx] = { ...next[idx], type: v };
                                setForm({ ...form, accessorials: next });
                              }}
                            >
                              <SelectTrigger className="sharp mt-1 h-9 apex-input" data-testid={`accessorial-type-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ACCESSORIAL_OPTIONS.map((o) => (
                                  <SelectItem key={o} value={o}>{o === "Other" ? "Other (Specify)" : o}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-9 md:col-span-5">
                            <label className="mono-label !text-[9px]">
                              {isOther ? "Description (required)" : "Notes (optional)"}
                            </label>
                            <Input
                              value={a.description}
                              onChange={(e) => {
                                const next = [...form.accessorials];
                                next[idx] = { ...next[idx], description: e.target.value };
                                setForm({ ...form, accessorials: next });
                              }}
                              placeholder={isOther ? "e.g. Wait time" : "e.g. 2 hours @ shipper"}
                              className="sharp mt-1 h-9 apex-input"
                              data-testid={`accessorial-desc-${idx}`}
                            />
                          </div>
                          <div className="col-span-3 md:col-span-2">
                            <label className="mono-label !text-[9px]">
                              Amount ({currencySymbol(form.currency)})
                            </label>
                            <Input
                              type="number"
                              step="0.01"
                              value={a.amount}
                              onChange={(e) => {
                                const next = [...form.accessorials];
                                next[idx] = { ...next[idx], amount: e.target.value };
                                setForm({ ...form, accessorials: next });
                              }}
                              className="sharp mt-1 h-9 apex-input font-mono"
                              data-testid={`accessorial-amount-${idx}`}
                            />
                          </div>
                          <div className="col-span-12 md:col-span-1 flex justify-end">
                            <button
                              type="button"
                              onClick={() => setForm({ ...form, accessorials: form.accessorials.filter((_, i) => i !== idx) })}
                              className="h-9 w-9 flex items-center justify-center border border-zinc-300 text-rose-700 hover:bg-rose-50"
                              title="Remove charge"
                              data-testid={`remove-accessorial-${idx}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex items-center justify-end gap-4 text-sm border-t border-zinc-200 pt-2">
                    <span className="mono-label">Accessorials subtotal</span>
                    <span className="font-mono font-semibold" data-testid="accessorials-subtotal">
                      {currencySymbol(form.currency)}
                      {form.accessorials.reduce((s, a) => s + (Number(a.amount) || 0), 0).toLocaleString()}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          <div>
            <label className="mono-label">Notes</label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sharp mt-1 apex-input" data-testid="job-notes" />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="sharp border-zinc-300" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="job-submit">
              {busy ? (isEdit ? "Saving..." : "Creating...") : (isEdit ? "Save changes" : "Create job")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
